/**
 * Storefront theming (PHASE 4). "Design tokens + section toggles, not a
 * theme engine" — a merchant picks a primary/accent color and a font
 * pairing from admin; this module is the single place that turns those
 * three values into actual CSS custom property overrides on <html>.
 *
 * Used by both the real bootstrap (ThemeBootstrap in App.js, driven by
 * saved settings) and the admin Theme page's live iframe preview (driven
 * by unsaved draft state via postMessage) — one source of truth for what
 * "applying a theme" means, so the preview can never drift from reality.
 */

/**
 * Expands shorthand (#abc) to full 6-digit form and validates the result
 * is genuinely hex. Malformed input (wrong length, non-hex characters)
 * falls back to black rather than silently producing NaN custom
 * properties — unreachable via the admin's <input type="color"> picker
 * (which only ever emits full 6-digit hex), but this is also called with
 * whatever a direct API write puts in the setting.
 */
function normalizeHex(hex) {
  let clean = String(hex || '').replace('#', '');
  if (clean.length === 3) {
    clean = clean.split('').map((c) => c + c).join('');
  }
  return /^[0-9a-fA-F]{6}$/.test(clean) ? clean : '000000';
}

function hexToHsl(hex) {
  const clean = normalizeHex(hex);
  const r = parseInt(clean.slice(0, 2), 16) / 255;
  const g = parseInt(clean.slice(2, 4), 16) / 255;
  const b = parseInt(clean.slice(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      default: h = (r - g) / d + 4;
    }
    h /= 6;
  }

  return { h: h * 360, s: s * 100, l: l * 100 };
}

function hslToHex(h, s, l) {
  const sat = s / 100;
  const light = l / 100;
  const c = (1 - Math.abs(2 * light - 1)) * sat;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = light - c / 2;
  let [r, g, b] = [0, 0, 0];

  if (h < 60) [r, g, b] = [c, x, 0];
  else if (h < 120) [r, g, b] = [x, c, 0];
  else if (h < 180) [r, g, b] = [0, c, x];
  else if (h < 240) [r, g, b] = [0, x, c];
  else if (h < 300) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];

  const toHex = (v) => Math.round((v + m) * 255).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

/** Darker variant for hover states — same hue/saturation, ~8% less light. */
function shadeHover(hex) {
  const { h, s, l } = hexToHsl(hex);
  return hslToHex(h, s, clamp(l - 8, 0, 100));
}

/** Soft tint for backgrounds — much lighter, slightly desaturated. */
function shadeSoft(hex) {
  const { h, s, l } = hexToHsl(hex);
  return hslToHex(h, clamp(s - 25, 0, 100), clamp(l + 40, 0, 96));
}

export const FONT_PAIRINGS = {
  classic: {
    label: 'Classic',
    description: 'Fraunces headings, Inter body — the default pairing.',
    heading: "'Fraunces Variable', 'Fraunces', Georgia, 'Times New Roman', serif",
    body: "'Inter Variable', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
  editorial: {
    label: 'Editorial',
    description: 'Playfair Display headings, Source Sans body — magazine feel.',
    heading: "'Playfair Display Variable', Georgia, 'Times New Roman', serif",
    body: "'Source Sans 3 Variable', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
  modern: {
    label: 'Modern',
    description: 'Manrope headings, Inter body — clean and geometric.',
    heading: "'Manrope Variable', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    body: "'Inter Variable', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
  // PHASE 7 — two more pairings backing the "Bold" and "Playful" look
  // presets below. Self-hosted the same way as the original three (see
  // frontend/src/index.js).
  bold: {
    label: 'Bold',
    description: 'Space Grotesk headings, DM Sans body — confident and geometric.',
    heading: "'Space Grotesk Variable', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    body: "'DM Sans Variable', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
  kristal: {
    label: 'Kristal',
    description: 'Bricolage Grotesque headings, Figtree body — friendly but businesslike.',
    heading: "'Bricolage Grotesque Variable', 'Figtree Variable', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    body: "'Figtree Variable', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
  playful: {
    label: 'Playful',
    description: 'Nunito throughout — rounded and friendly.',
    heading: "'Nunito Variable', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    body: "'Nunito Variable', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
};

const DEFAULT_PAIRING = 'classic';

/**
 * PHASE 7 — "look" presets. Each bundles a color pair + font pairing + a
 * sensible default for every homepage section toggle into one click in
 * the admin Theme page, rather than an operator hand-tuning 6+ separate
 * controls to get a coherent result. Applying a preset only seeds these
 * fields into the draft — hero copy/image and the new sections' own
 * content (testimonials, FAQ, split block, feature columns) are left
 * untouched, since a color/font change shouldn't silently wipe an
 * operator's writing.
 */
export const LOOK_PRESETS = {
  classic: {
    label: 'Classic',
    description: 'The original balanced look — forest green, warm terracotta.',
    primaryColor: '#2D4A3E',
    accentColor: '#A94F32',
    fontPairing: 'classic',
    sections: {
      hero: true, collections: true, bestsellers: true, reviews: true,
      testimonials: false, faq: false, split: false, featureColumns: false,
    },
  },
  minimal: {
    label: 'Minimal',
    description: 'Understated charcoal and slate blue — lets the products speak.',
    primaryColor: '#1F2937',
    accentColor: '#0EA5E9',
    fontPairing: 'modern',
    sections: {
      hero: true, collections: true, bestsellers: true, reviews: false,
      testimonials: false, faq: false, split: false, featureColumns: false,
    },
  },
  bold: {
    label: 'Bold',
    description: 'High-contrast indigo and amber with geometric type — makes a statement.',
    primaryColor: '#4C1D95',
    accentColor: '#F59E0B',
    fontPairing: 'bold',
    sections: {
      hero: true, collections: false, bestsellers: true, reviews: false,
      testimonials: true, faq: false, split: false, featureColumns: true,
    },
  },
  editorial: {
    label: 'Warm & Editorial',
    description: 'Magazine-style serif headlines with terracotta and deep green.',
    primaryColor: '#7A3B2E',
    accentColor: '#2D4A3E',
    fontPairing: 'editorial',
    sections: {
      hero: true, collections: true, bestsellers: false, reviews: true,
      testimonials: true, faq: false, split: true, featureColumns: false,
    },
  },
  kristal: {
    label: 'Kristal',
    description: 'Liquid glass over soft colour, firm rounded shapes and a clear blue.',
    primaryColor: '#1E5EFF',
    accentColor: '#0B7A5C',
    fontPairing: 'kristal',
    surfaceStyle: 'glass',
    sections: {
      hero: true, collections: true, bestsellers: true, reviews: true,
      testimonials: false, faq: false, split: false, featureColumns: false,
    },
  },
  playful: {
    label: 'Playful',
    description: 'Rounded type and vivid pink/yellow — friendly and approachable.',
    primaryColor: '#EC4899',
    accentColor: '#FBBF24',
    fontPairing: 'playful',
    sections: {
      hero: true, collections: true, bestsellers: true, reviews: false,
      testimonials: false, faq: true, split: false, featureColumns: true,
    },
  },
};

/**
 * Applies a theme onto document.documentElement's inline style, which
 * outranks any :root {} rule in a stylesheet for the same property —
 * no !important needed, and it's trivially reversible (removeProperty).
 */
const BUTTON_RADII = { square: '0px', soft: '6px', rounded: '999px' };
const CARD_RADII = { square: '0px', soft: '8px', rounded: '14px' };
const FORM_RADII = { square: '0px', soft: '4px', rounded: '999px' };
const CONTAINER_WIDTHS = { compact: '1120px', standard: '1280px', wide: '1440px' };
const SECTION_SPACING_CLASSES = ['compact', 'generous'];

export function applyTheme({ primaryColor, accentColor, backgroundColor, surfaceColor, softSurfaceColor, textColor, fontPairing, buttonRadius, cardRadius, formRadius, containerWidth, sectionSpacing, surfaceStyle }) {
  const root = document.documentElement.style;

  if (primaryColor) {
    root.setProperty('--brand', primaryColor);
    root.setProperty('--brand-hover', shadeHover(primaryColor));
    root.setProperty('--brand-soft', shadeSoft(primaryColor));
    root.setProperty('--theme-primary', primaryColor);
    root.setProperty('--theme-primary-hover', shadeHover(primaryColor));
  }

  if (accentColor) {
    root.setProperty('--accent', accentColor);
    root.setProperty('--accent-soft', shadeSoft(accentColor));
    root.setProperty('--theme-accent', accentColor);
  }

  if (backgroundColor) root.setProperty('--canvas-warm', backgroundColor);
  if (surfaceColor) root.setProperty('--canvas-white', surfaceColor);
  if (softSurfaceColor) root.setProperty('--canvas-soft', softSurfaceColor);
  if (textColor) {
    root.setProperty('--ink-primary', textColor);
    root.setProperty('--color-text', textColor);
  }

  const pairing = FONT_PAIRINGS[fontPairing] || FONT_PAIRINGS[DEFAULT_PAIRING];
  root.setProperty('--font-serif', pairing.heading);
  root.setProperty('--font-sans', pairing.body);
  root.setProperty('--button-radius', BUTTON_RADII[buttonRadius] || BUTTON_RADII.soft);
  root.setProperty('--radius-2', CARD_RADII[cardRadius] || CARD_RADII.soft);
  root.setProperty('--radius-3', CARD_RADII[cardRadius] || '12px');
  root.setProperty('--form-radius', FORM_RADII[formRadius] || FORM_RADII.soft);
  root.setProperty('--container-max-width', CONTAINER_WIDTHS[containerWidth] || CONTAINER_WIDTHS.standard);
  SECTION_SPACING_CLASSES.forEach((spacing) => document.documentElement.classList.remove(`theme-section-spacing--${spacing}`));
  if (SECTION_SPACING_CLASSES.includes(sectionSpacing)) document.documentElement.classList.add(`theme-section-spacing--${sectionSpacing}`);
  // Liquid-glass surfaces (styles/glass.css) switch on with one class.
  document.documentElement.classList.toggle('theme-surface--glass', surfaceStyle === 'glass');
}
